import React, { forwardRef, useEffect, useState } from 'react';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import { childSlots } from './blockPresentation';
import styles from './Workspace.module.scss';

const isCanvasDrag = transfer => Array.from(transfer?.types || []).some(type => (
  ['componenttype', 'existingnodeid'].includes(type.toLowerCase())
));

// Measured in viewport pixels, so zooming out does not make the drop targets tiny.
const sideWidth = bounds => Math.min(96, bounds.width / 3);
const OUTSIDE_DROP_MARGIN = 40;

const blockDropTarget = (event, canvas, block) => {
  const node = canvas?.nodes.find(item => item.id === block.dataset.previewNodeId);
  if (!node) return null;
  const bounds = block.getBoundingClientRect();
  const y = event.clientY - bounds.top;
  const x = event.clientX - bounds.left;
  // Reserve small caps for before/after, not a percentage of a potentially very tall grid.
  const cap = Math.min(12, bounds.height * 0.2);
  const outside = x < 0 || x > bounds.width;
  if (node.id !== canvas.root && bounds.width > 0
    && (outside || (y >= cap && y <= bounds.height - cap))
    && (x < sideWidth(bounds) || x > bounds.width - sideWidth(bounds))) {
    return { element: block, position: { nodeId: node.id, position: x < sideWidth(bounds) ? 'left' : 'right' } };
  }
  const fraction = y / Math.max(1, bounds.height);
  const slots = childSlots(node);
  const inside = slots.length && (node.id === canvas.root || (fraction >= 0.25 && fraction <= 0.75));
  return { element: block, position: {
    nodeId: node.id,
    position: inside ? 'inside' : fraction < 0.5 ? 'before' : 'after',
    ...(inside ? { slotKey: slots[0][0] } : {}),
  } };
};

/** Also accept gutters beside content; DOM hit targets alone leave dead zones there. */
export const previewDropTarget = (event, canvas) => {
  const stage = event.currentTarget;
  const area = stage.querySelector('[data-preview-canvas-area]');
  if (area && canvas?.root) {
    const bounds = area.getBoundingClientRect();
    const x = event.clientX;
    if (bounds.width > 0 && bounds.height > 0 && event.clientY >= bounds.top && event.clientY <= bounds.bottom
      && ((x <= bounds.left && x >= bounds.left - OUTSIDE_DROP_MARGIN)
        || (x >= bounds.right && x <= bounds.right + OUTSIDE_DROP_MARGIN))) {
      return { element: area, position: { nodeId: canvas.root, scope: 'canvas', position: x <= bounds.left ? 'left' : 'right' } };
    }
  }
  const block = event.target.closest('[data-preview-node-id]');
  const slot = event.target.closest('[data-preview-slot-owner]');
  const inSlot = slot && stage.contains(slot) && (!block || block.contains(slot));
  const slotTarget = inSlot && { element: slot, position: {
    nodeId: slot.dataset.previewSlotOwner, slotKey: slot.dataset.previewSlot, position: 'inside',
  } };
  if (inSlot) {
    const owner = canvas?.nodes.find(node => node.id === slot.dataset.previewSlotOwner);
    if (!owner?.slots?.[slot.dataset.previewSlot]?.length) return slotTarget;
  } else if (block && stage.contains(block)) {
    return blockDropTarget(event, canvas, block);
  }
  // Prefer the closest, most specific content block without crossing into another slot.
  const nearby = [...(inSlot ? slot : stage).querySelectorAll('[data-preview-node-id]')]
    .filter(element => element.dataset.previewNodeId !== canvas?.root)
    .map(element => {
      const bounds = element.getBoundingClientRect();
      const distance = Math.max(bounds.left - event.clientX, event.clientX - bounds.right, 0);
      return { element, bounds, distance };
    })
    .filter(({ bounds, distance }) => bounds.width > 0 && bounds.height > 0
      && event.clientY >= bounds.top && event.clientY <= bounds.bottom && distance <= OUTSIDE_DROP_MARGIN)
    .sort((a, b) => a.distance - b.distance || a.bounds.width * a.bounds.height - b.bounds.width * b.bounds.height);
  const target = nearby.length && blockDropTarget(event, canvas, nearby[0].element);
  return target || slotTarget || null;
};

/** Native DnD shared by the regular and fullscreen visual workspace. */
const PreviewDropSurface = forwardRef(({ canvas, disabled, onDropNode, picking = false, onPlace, children, className }, ref) => {
  const t = useLocalizedMessage();
  const [indicator, setIndicator] = useState(null);
  const clear = () => setIndicator(null);
  useEffect(() => {
    // A drag originating in the library ends outside this subtree, including Escape cancellation.
    const reset = () => setIndicator(null);
    document.addEventListener('dragend', reset);
    document.addEventListener('drop', reset);
    return () => {
      document.removeEventListener('dragend', reset);
      document.removeEventListener('drop', reset);
    };
  }, []);
  useEffect(() => { setIndicator(null); }, [picking, canvas, disabled]);
  const targetFor = event => !disabled && isCanvasDrag(event.dataTransfer) && previewDropTarget(event, canvas);
  const showTarget = (event, target) => {
    if (!target) { clear(); return; }
    const box = target.element.getBoundingClientRect();
    const stage = event.currentTarget.getBoundingClientRect();
    const { position } = target.position;
    const horizontal = ['left', 'right'].includes(position);
    const band = sideWidth(box);
    setIndicator({
      left: (position === 'right' ? box.right - band : box.left) - stage.left + event.currentTarget.scrollLeft,
      top: (position === 'after' ? box.bottom : box.top) - stage.top + event.currentTarget.scrollTop,
      width: horizontal ? band : box.width,
      height: ['inside', 'left', 'right'].includes(position) ? box.height : 3,
      position,
      scope: target.position.scope,
    });
  };
  const dragOver = event => {
    const target = targetFor(event);
    if (!target) { clear(); return; }
    event.preventDefault(); event.stopPropagation();
    event.dataTransfer.dropEffect = Array.from(event.dataTransfer.types).some(type => type.toLowerCase() === 'existingnodeid') ? 'move' : 'copy';
    showTarget(event, target);
  };
  return <div ref={ref} className={className} data-preview-viewport data-picking-location={picking || undefined} tabIndex={picking ? 0 : undefined}
    onMouseMove={event => { if (picking && !disabled) showTarget(event, previewDropTarget(event, canvas)); }}
    onMouseLeave={() => { if (picking) clear(); }}
    onClickCapture={event => {
      if (!picking) return;
      event.preventDefault(); event.stopPropagation();
      if (disabled) return;
      const target = previewDropTarget(event, canvas);
      clear();
      if (target && onPlace) onPlace(target.position);
    }}
    onDragOver={dragOver} onDragEnd={clear} onScroll={clear}
    onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) clear(); }}
    onDrop={event => {
      const target = targetFor(event);
      clear();
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      const nodeId = event.dataTransfer.getData('existingNodeId');
      const type = event.dataTransfer.getData('componentType');
      onDropNode(target.position, nodeId ? { nodeId } : { type });
    }}>
    {children}
    {indicator && <div aria-hidden="true" data-drop-position={indicator.position} data-drop-scope={indicator.scope} className={styles.dropIndicator}
      style={{ left: indicator.left, top: indicator.top, width: indicator.width, height: indicator.height }}>{indicator.scope === 'canvas' && <span className={styles.dropLabel}>{t('experience.design.entireLayout')}</span>}</div>}
  </div>;
});
export default PreviewDropSurface;
